import dayjs from "dayjs";
import { Link, useNavigate } from "react-router";
import { useState } from "react";
import axiosInstance, { api } from "../common/api";
import TablePage from "../components/TablePage";
import PdfViewer from "../common/PdfViewer";
import { date2locale } from "../common/common";
import { Button, Select, Tag } from "antd";
import { FileTextOutlined, UploadOutlined } from "@ant-design/icons";
import SimpleTablePage from "../common/SimpleTablePage";
import DebounceSelect from "../common/DebounceSelect";

const courseStatuses = {
  VALID: { color: "green", text: "Valido" },
  EXPIRED: { color: "red", text: "Scaduto" },
  PROGRAMMED: { color: "gray", text: "Programmato" },
  UPLOAD: { color: "blue", text: "Carica attestato" },
};

const columns = (setOpenCourse) => [
  {
    title: "ID",
    dataIndex: "id",
    fixed: "left",
    key: "id",
    width: 70,
    render: (id) => <Link to={"" + id}>{id}</Link>,
  },
  {
    title: "Categoria",
    key: "courseType",
    fixed: "left",
    dataIndex: ["courseType", "title"],
    filterSearch: true,
    ellipsis: true,
    width: 200,
  },
  {
    title: "Dipendente",
    fixed: "left",
    ellipsis: true,
    key: "name",
    dataIndex: ["employee", "fullName"],
    width: 250,
  },
  {
    key: "isbase",
    dataIndex: "isBase",
    title: "Tipo corso",
    ellipsis: true,
    width: 150,
    render: (v) => (v ? "Base" : "Aggiornamento"),
  },
  {
    title: "Data",
    key: "date",
    dataIndex: "date",
    sorter: true,
    ellipsis: true,
    width: 120,
    render: (date) => date2locale(date),
  },
  {
    title: "Stato",
    key: "status",
    ellipsis: true,
    width: 150,
    render: (record) => {
      const date = dayjs(record.date);
      const certDueDate = record.dueDate && dayjs(record.dueDate);
      const today = dayjs().startOf("day");
      const status = certDueDate
        ? certDueDate.isSameOrAfter(today)
          ? "VALID"
          : "EXPIRED"
        : date.isAfter(today)
          ? "PROGRAMMED"
          : "UPLOAD";
      return (
        <Tag variant={"outlined"} color={courseStatuses[status].color}>
          {courseStatuses[status].text}
        </Tag>
      );
    },
  },
  {
    title: "Docente",
    key: "instructor",
    dataIndex: "instructor",
    ellipsis: true,
    width: 200,
  },
  {
    title: "Indirizzo",
    key: "location",
    dataIndex: "location",
    ellipsis: true,
    width: 200,
  },
  {
    title: "Note",
    key: "notes",
    dataIndex: "notes",
    ellipsis: true,
    width: 300,
  },
  {
    title: "Data attestato",
    key: "certificateDate",
    dataIndex: "certificateDate",
    sorter: true,
    ellipsis: true,
    width: 150,
    render: (dueDate) => dueDate && date2locale(dueDate),
  },
  {
    title: "Data scadenza attestato",
    key: "dueDate",
    dataIndex: "dueDate",
    sorter: true,
    ellipsis: true,
    width: 150,
    render: (dueDate) => dueDate && date2locale(dueDate),
  },
  {
    title: "Attestato",
    key: "doc",
    width: 150,
    ellipsis: true,
    render: (_, item) =>
      item.ref && (
        <Button
          size="small"
          icon={<FileTextOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            setOpenCourse(encodeURI(`/course/${item.id}/file`));
          }}
        />
      ),
  },
];

const filterItems = [
  {
    name: "status",
    label: "Stato",
    readableValue: (value) => courseStatuses[value]?.text ?? value,
    formItem: {
      children: (
        <Select
          allowClear
          options={Object.entries(courseStatuses).map(([value, { text }]) => ({
            value,
            label: text,
          }))}
        />
      ),
    },
  },
  {
    name: "employee",
    label: "Dipendente",
    readableValue: (value) => value && JSON.parse(value).label,
    formItem: {
      props: {
        getValueProps: (value) => ({ value: value && JSON.parse(value) }),
        normalize: (value) => value && JSON.stringify(value),
      },
      children: (
        <DebounceSelect
          fetchOptions={(query) =>
            api.employee
              .list({ query, pageSize: 9999 })
              .then(({ data }) => data.map((x) => ({ label: x.fullName, value: x.id })))
          }
        />
      ),
    },
  },
  {
    name: "courseType",
    label: "Categoria",
    readableValue: (value) => value && JSON.parse(value).label,
    formItem: {
      props: {
        getValueProps: (value) => ({ value: value && JSON.parse(value) }),
        normalize: (value) => value && JSON.stringify(value),
      },
      children: (
        <DebounceSelect
          fetchOptions={(query) =>
            api.courseType
              .list({ query, pageSize: 9999 })
              .then(({ data }) => data.map((x) => ({ label: x.title, value: x.id })))
          }
        />
      ),
    },
  },
];
export default function () {
  const [openCourse, setOpenCourse] = useState();
  const navigate = useNavigate();

  const fetch = (params) => {
    return api.course.list({
      ...params,
      employee: params.employee && JSON.parse(params.employee).value,
      courseType: params.courseType && JSON.parse(params.courseType).value,
    });
  };
  return (
    <div>
      <PdfViewer url={openCourse} onClose={() => setOpenCourse(null)} />
      <SimpleTablePage
        title={"Corsi di formazione"}
        description={"Gestisci e monitora i corsi di formazione del personale"}
        columns={columns(setOpenCourse)}
        fetchFunction={fetch}
        resource="course"
        filterItems={filterItems}
        actionsMenu={{
          items: (record) =>
            record.certificateDate == null
              ? [{ key: "uploadCertificate", label: "Carica attestato", icon: <UploadOutlined /> }]
              : [],
          onClick: (key, record) => {
            if (key === "uploadCertificate") navigate(`${record.id}/certificate`);
          },
        }}
      />
    </div>
  );
}
